import React, { useMemo, useState } from 'react';
import { styled, Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useAppSelector } from '@/redux/hooks';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { StyledPaper } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import { ElementInventoryStatus, ElementType } from '@/redux/slices/slice_types';
import PolarisIcon from '@/components/_common/PolarisIcon';
import ProgressBar from '@/components/_common/Table/Calendar/CalendarSharedComponents/ProgressBar';
import ElementsByTypeAccordion from '@/components/_common/Table/Calendar/SidePanel/components/ElementsByTypeAccordion';
import {
  StatusFilter,
  elementStatusToFilterStatusMap,
  statusFilterToElementStatusesMap
} from '@/components/_common/Table/Calendar/SidePanel/components/ElementReadiness.constants';
import StatusFilterComponent from '@/components/_common/Table/Calendar/SidePanel/components/StatusFilter';
import PlatformFilter from '@/components/_common/Table/Calendar/SidePanel/components/PlatformFilter';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';
import { ReadinessElement } from '@/components/_common/Table/Calendar/Calendar.types';
import { selectAvailPlatform } from '@/redux/selectors/constantsSelectors';

interface Props {
  loadedAt: Date;
  defaultSelectedFilters: {
    platform: string[];
    status: ElementInventoryStatus[];
  };
  elements: ReadinessElement[];
  platformsList: string[];
}

const getGroupsInOrder = (elementsByType: Partial<Record<ElementType, ReadinessElement[]>>) => {
  const { video, audio, timedtext, metadata, ...restGroups } = { ...elementsByType };
  const orderedGroups: { type: ElementType; elements: ReadinessElement[] }[] = [];

  if (video) orderedGroups.push({ type: 'video', elements: video });
  if (audio) orderedGroups.push({ type: 'audio', elements: audio });
  if (timedtext) orderedGroups.push({ type: 'timedtext', elements: timedtext });
  if (metadata) orderedGroups.push({ type: 'metadata', elements: metadata });

  Object.entries(restGroups)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([_type, elements]) => {
      const type = _type as ElementType;
      orderedGroups.push({ type, elements });
    });

  return orderedGroups;
};

const ElementReadiness: React.FC<Props> = ({ loadedAt, defaultSelectedFilters, elements, platformsList }) => {
  const theme = useFocusTheme();
  const { date_format, time_format } = useAppSelector(selectUserPreferences);
  const allPlatforms = useAppSelector(selectAvailPlatform);
  const { platform: defaultPlatforms, status: defaultStatuses } = defaultSelectedFilters;
  const [appliedFilters, setAppliedFilters] = useState<{ status: ElementInventoryStatus[]; platform: string[] }>({
    status: defaultStatuses,
    platform: defaultPlatforms
  });

  const filteredElements = useMemo(() => {
    if (!appliedFilters.platform.length) {
      return elements;
    }

    return elements.filter((element) => appliedFilters.platform.includes(element.platform));
  }, [elements, appliedFilters.platform]);

  // We calculate completion stats only based on elements filtered by selected platforms.
  // Status filter should not affect `total`, `completed`, `completedPercentage` values.
  const { total, completed, completedPercentage } = useMemo(() => {
    const total = filteredElements.length;
    const completed = filteredElements.filter((el) => el.inventory_status === ElementInventoryStatus.Available).length;
    return {
      total,
      completed,
      completedPercentage: Math.floor((completed / total) * 100)
    };
  }, [filteredElements]);

  const elementsSortedByLanguage = useMemo(() => filteredElements.sort((a, b) => a.language.localeCompare(b.language)), [filteredElements]);

  const elementsByType = useMemo(() => {
    return Object.groupBy(elementsSortedByLanguage, (el) => el.element_type);
  }, [elementsSortedByLanguage]);

  const orderedGroups = useMemo(() => getGroupsInOrder(elementsByType), [elementsByType]);

  const { expandedGroups, toggleGroup, expandAll, collapseAll } = useCollapsableGroups({ allTypes: Object.keys(elementsByType) as ElementType[] });

  const handleStatusFilterChange = (value: StatusFilter[]) => {
    setAppliedFilters({
      ...appliedFilters,
      status: value.flatMap((s) => statusFilterToElementStatusesMap[s])
    });
  };

  const handlePlatformChange = (values: string[]) => {
    setAppliedFilters({
      ...appliedFilters,
      platform: values
    });
  };

  const statusFilterValues = appliedFilters.status.reduce<StatusFilter[]>((acc, cur) => {
    if (!acc.includes(elementStatusToFilterStatusMap[cur])) {
      acc.push(elementStatusToFilterStatusMap[cur]);
    }
    return acc;
  }, []);

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <PlatformFilter
          values={appliedFilters.platform}
          options={platformsList.map((p) => ({ label: allPlatforms[p], value: p }))}
          onChange={handlePlatformChange}
          fullWidth
        />
      </Box>
      <StyledPaper big>
        <HeaderGrid container>
          <Grid item xs>
            Element Readiness {completedPercentage}%
          </Grid>
          <Grid item>
            <Tooltip title={`${completed}/${total} ready for delivery`}>
              <HelpIcon icon="question_outline" />
            </Tooltip>
          </Grid>
        </HeaderGrid>
        <ProgressWrapper>
          <ProgressBar
            value={completedPercentage}
            height={12}
            background={`linear-gradient(40deg, ${theme.palette.tertiary.main}, ${theme.palette.primary.main})`}
          />
        </ProgressWrapper>
        <ControlsGrid container>
          <ControlsLeft item xs>
            <Box>
              <ExpandCollapseButton onClick={expandAll}>Expand</ExpandCollapseButton>
              {' | '}
              <ExpandCollapseButton onClick={collapseAll}>Collapse</ExpandCollapseButton>
            </Box>
          </ControlsLeft>
          <Grid item>
            <StatusFilterComponent values={statusFilterValues} onChange={handleStatusFilterChange} />
          </Grid>
        </ControlsGrid>

        {orderedGroups.map(({ type, elements }) => (
          <ElementsByTypeAccordion
            key={type}
            isOpen={expandedGroups.includes(type)}
            type={type}
            elements={elements}
            statusFilter={appliedFilters.status}
            toggle={() => toggleGroup(type)}
          />
        ))}

        {date_format && time_format && (
          <UpdatedText>
            Updated as of {format(loadedAt, date_format)} {format(loadedAt, time_format)}
          </UpdatedText>
        )}
      </StyledPaper>
    </>
  );
};

export default ElementReadiness;

const HeaderGrid = styled(Grid)`
  margin-bottom: 12px;
  align-items: center;
  font-size: 1.143rem;
`;

const HelpIcon = styled(PolarisIcon)`
  position: relative;
  top: 2px;
  color: ${({ theme }) => (theme as FocusTheme).customOverrides.Calendar.textSecondary};
`;

const ProgressWrapper = styled(Box)`
  margin-top: 12px;
  margin-bottom: 28px;
`;

const ControlsGrid = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 8px;
`;

const ControlsLeft = styled(Grid)`
  flex-shrink: 0;
  margin-left: -4px;
  font-size: 0.857rem;
  display: flex;
  align-items: end;
`;

const UpdatedText = styled(Typography)`
  margin-top: 16px;
  font-size: 0.857rem;
  color: ${({ theme }) => (theme as FocusTheme).customOverrides.Calendar.textSecondary};
`;

const ExpandCollapseButton = styled(Button)`
  min-width: auto;
  padding: 0 ${({ theme }) => theme.spacing(0.5)};
  font-size: 0.857rem;
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark)};
`;

const useCollapsableGroups = ({ allTypes }: { allTypes: ElementType[] }) => {
  const [expandedGroups, setExpandedGroups] = React.useState<ElementType[]>([]);

  const expandGroup = (type: ElementType) => {
    if (!expandedGroups.includes(type)) {
      setExpandedGroups([...expandedGroups, type]);
    }
  };

  const collapseGroup = (type: ElementType) => {
    return setExpandedGroups(expandedGroups.filter((t) => t !== type));
  };

  const toggleGroup = (type: ElementType) => {
    if (expandedGroups.includes(type)) {
      collapseGroup(type);
    } else {
      expandGroup(type);
    }
  };

  const expandAll = () => {
    setExpandedGroups(allTypes);
  };

  const collapseAll = () => {
    setExpandedGroups([]);
  };

  return {
    expandedGroups,
    toggleGroup,
    expandAll,
    collapseAll
  };
};
